import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { initializeDefaultUser } from '../utils/authServiceUtil';
import { LOADING_TIMEOUT_MS } from '../constants/bookingConst';

const AuthStatusContext = createContext(null);

/**
 * Starts the visitor's anonymous sign-in on mount and shares its progress,
 * WITHOUT blocking anything from rendering.
 *
 * Why this is not a blocking guard: on a first visit the sign-in waits on
 * App Check (reCAPTCHA Enterprise loads, scores the visitor, and the result
 * is exchanged for a token) and then two Identity Toolkit round trips —
 * about 3.5s measured on a fast connection. The landing page can render from
 * the build-time config snapshot without any of that, so only the routes and
 * reads that touch the database wait on `ready` (see AuthGuard,
 * ConfigProvider and BookingApp).
 *
 * The bootstrap is raced against LOADING_TIMEOUT_MS because the Auth SDK
 * applies no client-side timeout: a blocked IndexedDB read or a sign-in
 * swallowed by a captive portal would otherwise stay pending forever.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // 'loading' -> 'ready' | 'failed'. One status rather than separate flags,
  // so there is no combination that means nothing.
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // `active` makes the first outcome the final one, so a stalled sign-in
    // that lands after the timeout cannot yank the error screen away.
    let active = true;
    setStatus('loading');

    const timer = setTimeout(() => {
      if (!active) return;
      active = false;
      console.error(`App startup auth exceeded ${LOADING_TIMEOUT_MS}ms`);
      setStatus('failed');
    }, LOADING_TIMEOUT_MS);

    initializeDefaultUser()
      .then((firebaseUser) => {
        if (!active) return;
        active = false;
        clearTimeout(timer);
        setUser(firebaseUser);
        setStatus(firebaseUser ? 'ready' : 'failed');
      })
      .catch((error) => {
        if (!active) return;
        active = false;
        clearTimeout(timer);
        console.error('App startup auth failed:', error);
        setStatus('failed');
      });

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // `attempt` is deliberately unread: it only exists so retry() re-runs
    // the bootstrap from the top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  /**
   * Runs the sign-in again from scratch. Wired to every Try Again button
   * that sits on an auth failure.
   *
   * @returns {void}
   */
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const value = useMemo(() => ({
    user,
    status,
    ready: status === 'ready' && !!user,
    failed: status === 'failed' || (status === 'ready' && !user),
    retry,
  }), [user, status, retry]);

  return (
    <AuthStatusContext.Provider value={value}>
      {children}
    </AuthStatusContext.Provider>
  );
}

/**
 * Reads the visitor's sign-in progress.
 *
 * @returns {{
 *   user: import('firebase/auth').User|null,
 *   status: 'loading'|'ready'|'failed',
 *   ready: boolean,
 *   failed: boolean,
 *   retry: () => void,
 * }}
 */
export function useAuthStatus() {
  const context = useContext(AuthStatusContext);
  if (!context) {
    throw new Error('useAuthStatus must be used within an AuthProvider');
  }
  return context;
}
